import {
  Box,
  Button,
  CircularProgress,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import { ApiError, getMessageFromError } from '@pro4-erp/core-web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import WorkIcon from '@mui/icons-material/Work';
import { NextCloudLink, VisitProductNC } from '../types';
import { getVisitProducts } from '../api/getVisitProducts';
import { getProductLinkNextCloud } from '../api/getProductLinkNextCloud';

const ProductItem = ({ item, woUid }: { item: VisitProductNC; woUid: string }) => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { enqueueSnackbar } = useSnackbar();
  const { mutateAsync } = useMutation<NextCloudLink, ApiError>({
    mutationFn: () => getProductLinkNextCloud(item.QnapDirectory, woUid),
    mutationKey: ['product-link-id', item.QnapDirectory, woUid],
    onSuccess: (data) => {
      if (!data.status) {
        return;
      }
      window.open(data.url, '_blank');
    },
    onError: (error: Error) => {
      enqueueSnackbar(getMessageFromError(error), { variant: 'error' });
    },
    cacheTime: 1000 * 5 * 60,
  });
  const getProductLink = async () => {
    const mutation = queryClient.getMutationCache().find<NextCloudLink>({
      mutationKey: ['product-link-id', item.QnapDirectory, woUid],
    });
    const link = mutation?.state?.data?.status && mutation?.state?.data?.url;
    if (!link) {
      mutateAsync();
      return;
    }
    window.open(link, '_blank');
  };
  return (
    <ListItem
      sx={{ maxWidth: '60vw' }}
      secondaryAction={
        <Button
          variant="contained"
          onClick={() => {
            getProductLink();
          }}
        >
          {t('Product files', { defaultValue: 'Product files' })}
        </Button>
      }
    >
      <ListItemAvatar>
        <Avatar>
          <WorkIcon />
        </Avatar>
      </ListItemAvatar>
      <ListItemText primary={item.Name} />
    </ListItem>
  );
};

const VisitProducts = ({ visitId, woUid }: { visitId: string; woUid: string }) => {
  const query = useQuery<VisitProductNC[], ApiError>(
    ['visit-products-id', visitId],
    () => getVisitProducts(visitId),
    { refetchOnWindowFocus: false, staleTime: 1000 * 5 * 60 }
  );

  if (query.isFetching) {
    return (
      <Box>
        <CircularProgress />
      </Box>
    );
  }
  return (
    <List sx={{ mx: '4rem' }}>
      {query.error && <ListItemText primary="ALARM ERROR" />}
      {query.isFetched &&
        query.data &&
        query.data.map((item) => {
          return <ProductItem key={item.Name} item={item} woUid={woUid} />;
        })}
    </List>
  );
};

export default VisitProducts;
